import {
  type IdeaRole,
  NOTE_QUESTION,
  NOTE_SUBJECT,
  noteFooter,
  noteHeader,
} from './tradeIdeaVocab'
import { WorkbenchNoteBox, type NoteBoxClose } from './WorkbenchNoteBox'

/**
 * The note box for a trade-idea level.
 *
 * One shape for all three levels: **heading · text area · one full-width button
 * that carries the flow**. It advances while the idea is still missing
 * something and finishes it once nothing is left to ask for — see `noteFooter`,
 * which owns that decision so the label and the act cannot drift apart.
 *
 * There is no Cancel and no Save-this-note, and that is not an omission — the
 * note saves itself as it is typed, so a button offering to "save" it would
 * describe work it is not doing, and one offering to "cancel" would offer to
 * undo an act the trader never asked for. The honest way to decline is to type
 * nothing and close. ("Save trade idea" saves the IDEA, not the sentence.)
 *
 * The bin in the header is NOT that Cancel. It destroys the LEVEL — an entry,
 * stop or target that exists on the chart whether this box is open or not —
 * rather than declining to write a sentence, and it leaves through the same one
 * exit carrying a flag, like every other button here.
 *
 * The card, the drag, the clamp, the debounced save, the click-away and the
 * phone drawer all live in `WorkbenchNoteBox`, shared with the drawing comment
 * box. THIS file is only the vocabulary and the buttons — what makes a trade
 * level's box different from a drawing's is what it asks for, not how it looks.
 *
 * This is NOT `WorkbenchEntryComposer`. That component belongs to the legacy
 * buy/sell marker tool and keeps its own shape, words and modal behaviour; the
 * two tools share no vocabulary, by design. See the header of `legTypes.ts`.
 */

export interface IdeaNoteExit extends NoteBoxClose {
  /** "Next" — move the flow on to whatever the idea still needs. */
  advance?: boolean
  /** "Save trade idea" — finish the idea, not just this level. */
  finish?: boolean
  /**
   * The bin — destroy THIS level, and only this one. The note goes with it,
   * unsaved: writing a sentence into an overlay on its way out is work nobody
   * asked for. Rides the same exit as the rest so the box is torn down, and the
   * baton dropped, in one place.
   */
  deleteMark?: boolean
  /** "Add another take profit" — re-arm the same step. */
  addAnother?: boolean
}

export interface WorkbenchIdeaNoteProps {
  role: IdeaRole
  /**
   * What the idea still wants after this level — `pendingStep(idea, role)`.
   * Null means nothing, and the footer offers to finish instead of to advance.
   * Passed in rather than derived here: the chart owns the idea, and a box that
   * worked out its own answer would be a second lookup free to disagree.
   */
  nextStep: IdeaRole | null
  /**
   * This exact level's name — "Stop loss", "Take profit 2" — as the panel row
   * and the line on the canvas spell it. Passed in for the same reason
   * `nextStep` is: the chart owns the marks, and a box that rebuilt the name
   * from its role alone could not tell three take profits apart.
   */
  label?: string
  /** Opening position, relative to the chart wrapper. */
  x: number
  y: number
  initialValue?: string
  /** Debounced while typing. */
  onSave: (text: string) => void
  /** The ONE exit. Every dismissal and every button comes through here. */
  onCommit: (text: string, exit?: IdeaNoteExit) => void
}

export function WorkbenchIdeaNote({
  role,
  nextStep,
  label,
  x,
  y,
  initialValue = '',
  onSave,
  onCommit,
}: WorkbenchIdeaNoteProps) {
  const footer = noteFooter(role, nextStep)

  return (
    <WorkbenchNoteBox<IdeaNoteExit>
      title={noteHeader(role)}
      placeholder={NOTE_QUESTION[role]}
      x={x}
      y={y}
      initialValue={initialValue}
      markerAttr="data-idea-note"
      // Not on the entry: the idea takes its name, colour, direction and
      // geometry lock from that mark, so one unconfirmed click must not be able
      // to behead it. The entry stays deletable from the panel row, where the
      // bin is hover-revealed — deliberate friction.
      destructive={
        role === 'entry'
          ? null
          : {
              // The same words as the trade-idea panel's bin for the same act,
              // so the two routes to it cannot be found by different names.
              // Naming the exact level is also how this button PROVES it takes
              // only the target you opened — it is not confirmed, so it must be
              // legible.
              ariaLabel: `Delete ${label || NOTE_SUBJECT[role]}`,
              exit: { deleteMark: true },
            }
      }
      // The flag is read off the SAME answer that wrote the label, so the button
      // can never do one thing and say another: it advances exactly when it
      // reads "Next".
      primary={{
        label: footer.primary,
        // "Save trade idea" is three words written for a full-width button with
        // a row to itself. Sharing a phone header row with a truncating title,
        // the verb is the part that matters — and the noun is already the
        // subject of the header it sits on.
        phoneLabel: footer.advances ? footer.primary : 'Save',
        exit: footer.advances ? { advance: true } : { finish: true },
      }}
      // Saving leads: the trade idea is the object being built and saving is
      // what completes it. Another target is an addition to that object, not a
      // rival to finishing it — and most ideas have one.
      secondary={
        footer.addAnother ? { label: 'Add another take profit', exit: { addAnother: true } } : null
      }
      onSave={onSave}
      onCommit={onCommit}
    />
  )
}
